import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/common/data-table";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Recommendation } from "@/hooks/use-kb-insights";

// `KB` and `CATALOGUE` come from a single pre-vocabulary batch and are broader
// than the modern kinds they resemble, so they keep distinct labels.
const KIND_LABELS = new Map([
  ["deflectable", "KB"],
  ["structured_escalation", "Catalog"],
  ["automatable", "Workflow"],
  ["automatable_with_approval", "Workflow+Approval"],
  ["KB", "KB (legacy)"],
  ["CATALOGUE", "Catalog (legacy)"],
]);

const STATUS_COLORS = new Map([
  ["DRAFT", "bg-yellow-100 text-yellow-700"],
  ["APPROVED", "bg-green-100 text-green-700"],
  ["DISMISSED", "bg-gray-100 text-gray-500"],
  ["DRIFTED", "bg-orange-100 text-orange-700"],
]);

function getClassificationLabel(value: string | boolean | null): string {
  if (value === true) return "Deflectable";
  if (value === false || value === null) return "—";
  switch (value) {
    case "deflectable":
      return "Deflectable";
    case "potentially_deflectable":
      return "Potentially Deflectable";
    case "partially_deflectable":
      return "Partially Deflectable";
    case "not_deflectable":
      return "Not Deflectable";
    case "automatable":
      return "Automatable";
    case "automatable_with_approval":
      return "Auto+Approval";
    case "structured_escalation":
      return "Escalation";
    case "junk":
      return "Junk";
    default:
      return value;
  }
}

const CLASSIFICATION_TOOLTIPS = new Map([
  [
    "partially_deflectable",
    "In some cases there were opportunities for deflection, in others there weren't",
  ],
  [
    "potentially_deflectable",
    "No evidence it would lead to deflections, but there is evidence the article arose from conversation deflection attempts",
  ],
]);

function getClassificationColor(value: string | boolean | null): string {
  if (value === true || value === "deflectable") {
    return "text-green-600 border-green-300";
  }
  if (value === "potentially_deflectable") {
    return "text-emerald-600 border-emerald-300";
  }
  if (value === "partially_deflectable") {
    return "text-amber-600 border-amber-300";
  }
  if (value === "not_deflectable") {
    return "text-muted-foreground border-border";
  }
  if (value === "automatable") {
    return "text-blue-600 border-blue-300";
  }
  if (value === "automatable_with_approval") {
    return "text-violet-600 border-violet-300";
  }
  if (value === "structured_escalation") {
    return "text-muted-foreground border-border";
  }
  if (value === "junk") {
    return "text-muted-foreground border-border";
  }
  return "";
}

export function createReadOnlyColumns(): ColumnDef<Recommendation>[] {
  return [
    {
      size: 280,
      id: "title",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Title" />
      ),
      cell: ({ row }) => {
        const c = row.original.content as unknown as Record<string, unknown>;
        const title = (c.title ?? c.name ?? "") as string;
        const intent = row.original.representative_intent;
        const showIntent = intent && intent !== title;
        return (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{title}</p>
            {showIntent && (
              <p className="text-muted-foreground mt-0.5 truncate text-xs">
                {intent}
              </p>
            )}
          </div>
        );
      },
    },
    {
      size: 90,
      id: "kind",
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Kind" />
      ),
      cell: ({ row }) => {
        const kind = (row.original as Record<string, unknown>).kind as
          | string
          | undefined;
        const label = kind ? (KIND_LABELS.get(kind) ?? kind) : "—";
        return (
          <Badge variant="secondary" className="text-[10px]">
            {label}
          </Badge>
        );
      },
    },
    {
      size: 70,
      accessorKey: "status",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS.get(status) ?? ""}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      size: 90,
      accessorKey: "self_serviceable",
      id: "self_serviceable",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Classification" />
      ),
      cell: ({ row }) => {
        const value = row.original.self_serviceable;
        const label = getClassificationLabel(value);
        if (label === "—")
          return <span className="text-muted-foreground text-xs">—</span>;
        const tooltip =
          typeof value === "string"
            ? CLASSIFICATION_TOOLTIPS.get(value)
            : undefined;
        const badge = (
          <Badge
            variant="outline"
            className={`text-[10px] ${getClassificationColor(value)}`}
          >
            {label}
          </Badge>
        );
        if (!tooltip) return badge;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{badge}</TooltipTrigger>
              <TooltipContent side="top" className="max-w-64">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      size: 55,
      id: "conversation_count",
      accessorKey: "conversation_count",
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Sources" />
      ),
      cell: ({ row }) => {
        const count =
          row.original.conversation_count ??
          row.original.evidence.conversation_count;
        return <span className="text-sm tabular-nums">{count}</span>;
      },
    },
    {
      size: 55,
      accessorKey: "source",
      id: "source",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Source" />
      ),
      cell: ({ row }) => {
        const source = (row.original as Record<string, unknown>).source as
          | string
          | undefined;
        return (
          <span className="text-muted-foreground text-[10px]">
            {source === "tickets" ? "Tickets" : "Convos"}
          </span>
        );
      },
    },
  ];
}
